/**
 * Locale registry.
 *
 * Mirrors config/laravellocalization.php: only `et` and `ru` are enabled, `et`
 * is the default and its prefix is hidden from the URL
 * (`hideDefaultLocaleInURL => true`).
 */

export const SUPPORTED_LOCALES = ['et', 'ru'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Deliberately typed as the literal `'et'` rather than as `Locale`: it is used
 * to index the per-locale slug maps, whose Estonian entry is the only required
 * one. Widening it to `Locale` would make every lookup `string | undefined`.
 */
export const DEFAULT_LOCALE = 'et' satisfies Locale;

export type DefaultLocale = typeof DEFAULT_LOCALE;

/** `et` lives at `/`, `ru` at `/ru`. */
export const HIDE_DEFAULT_LOCALE_IN_URL = true;

interface LocaleMeta {
  /** Value for `<html lang>`. */
  htmlLang: string;
  /** Label in the language switcher (was hardcoded `est`/`rus` in Blade). */
  shortLabel: string;
  /** Google Fonts family, per the conditional in master.blade.php. */
  fontFamily: string;
}

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  et: {
    htmlLang: 'et',
    shortLabel: 'est',
    fontFamily: 'Titillium Web',
  },
  ru: {
    htmlLang: 'ru',
    shortLabel: 'rus',
    fontFamily: 'Alegreya Sans',
  },
};

export function isLocale(value: unknown): value is Locale {
  return (
    typeof value === 'string' &&
    (SUPPORTED_LOCALES as readonly string[]).includes(value)
  );
}

/** URL prefix for a locale, e.g. `''` for `et` and `/ru` for `ru`. */
export function localePrefix(locale: Locale): string {
  if (HIDE_DEFAULT_LOCALE_IN_URL && locale === DEFAULT_LOCALE) {
    return '';
  }
  return `/${locale}`;
}
